import React from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { Info } from 'lucide-react'
import useLocaleText from 'components/useLocaleText'
import { CHART_COLORS } from 'utils/taskAnalytics'

export type ReasonsVariant = 'rejected' | 'warning' | 'skipped'

interface ReasonsCollapsibleProps {
  reasons: string[]
  variant?: ReasonsVariant
  defaultExpanded?: boolean
}

export const ReasonsCollapsible: React.FC<ReasonsCollapsibleProps> = ({ reasons, variant = 'warning' }) => {
  if (!reasons || reasons.length === 0) {
    return null
  }

  // Define colors depending on the variant
  let bgColor: string
  let textColor: string

  switch (variant) {
    case 'rejected':
      bgColor = CHART_COLORS.rejected // light-red
      textColor = CHART_COLORS.rejectedText // dark-red
      break
    case 'warning':
    case 'skipped':
      bgColor = CHART_COLORS.inProgress // yellow
      textColor = CHART_COLORS.inProgressText // dark-yellow/orange
      break
    default:
      bgColor = CHART_COLORS.inProgress
      textColor = CHART_COLORS.inProgressText
  }

  return (
    <Box
      mb="20px"
      bg={bgColor}
      borderRadius="8px"
      overflow="hidden"
      style={{
        borderInlineStart: `4px solid ${textColor}`,
      }}
    >
      <Flex align="flex-start" px="12px" py="10px" style={{ gap: '10px' }}>
        <Box flexShrink={0} color={textColor} mt="2px">
          <Info size={18} />
        </Box>
        <Flex direction="column" style={{ gap: '4px' }}>
          {reasons.map((reasonKey, index) => (
            <ReasonBullet key={index} reasonKey={reasonKey} textColor={textColor} />
          ))}
        </Flex>
      </Flex>
    </Box>
  )
}

// Child component for one reason bullet
interface ReasonBulletProps {
  reasonKey: string
  textColor: string
}

const ReasonBullet: React.FC<ReasonBulletProps> = ({ reasonKey, textColor }) => {
  const reasonText = useLocaleText(reasonKey, true)

  return (
    <Flex align="flex-start" style={{ gap: '8px' }}>
      <Box w="5px" h="5px" bg={textColor} borderRadius="50%" flexShrink={0} mt="7px" />
      <Text
        fontSize="13px"
        fontWeight="400"
        color={textColor}
        lineHeight="1.5"
        wordBreak="break-word"
        textTransform="uppercase"
        letterSpacing="0.3px"
      >
        {reasonText}
      </Text>
    </Flex>
  )
}
